
import { supabase } from '@/integrations/supabase/client';

// Функция для удаления одной подписки без реквизитов карты
export const deleteSubscriptionWithoutPaymentMethod = async (subscriptionId: string) => {
  try {
    console.log(`Попытка удаления подписки без реквизитов карты: ${subscriptionId}`);
    
    // Используем прямой SQL вместо rpc для избежания проблем с типизацией
    const { data, error } = await supabase
      .from('subscriptions')
      .select('payment_method_id, card_last4, status')
      .eq('id', subscriptionId)
      .single();
    
    if (error) {
      console.error('Ошибка при получении подписки:', error);
      return { success: false, error: error.message };
    }
    
    // Проверяем, что подписка соответствует критериям для удаления
    if (data.payment_method_id || data.card_last4 || 
        (data.status !== 'cancelled' && data.status !== 'payment_update_needed')) {
      return { 
        success: false, 
        error: 'Подписка не соответствует критериям для удаления (должна быть отменена и без реквизитов карты)' 
      };
    }
    
    // Удаляем связанные платежи со статусом error или cancelled
    const { error: paymentsError } = await supabase
      .from('payments')
      .delete()
      .eq('subscription_id', subscriptionId)
      .in('status', ['error', 'cancelled']);
    
    if (paymentsError) {
      console.error('Ошибка при удалении платежей:', paymentsError);
      return { success: false, error: paymentsError.message };
    }
    
    // Удаляем саму подписку
    const { error: subscriptionError } = await supabase
      .from('subscriptions')
      .delete()
      .eq('id', subscriptionId);
    
    if (subscriptionError) {
      console.error('Ошибка при удалении подписки:', subscriptionError);
      return { success: false, error: subscriptionError.message };
    }
    
    console.log('Подписка успешно удалена:', subscriptionId);
    return { success: true, data: { deleted: true } };
  } catch (err: any) {
    console.error('Непредвиденная ошибка при удалении подписки:', err);
    return { success: false, error: err.message };
  }
};

// Функция для пакетного удаления всех подписок без реквизитов карты
export const cleanupSubscriptionsWithoutPaymentMethods = async () => {
  try {
    console.log('Запуск пакетного удаления подписок без реквизитов карты');
    
    // Получаем все подписки без реквизитов карты
    const { data: subscriptions, error: fetchError } = await supabase
      .from('subscriptions')
      .select('id')
      .is('payment_method_id', null)
      .is('card_last4', null)
      .in('status', ['cancelled', 'payment_update_needed']);
    
    if (fetchError) {
      console.error('Ошибка при получении подписок:', fetchError);
      return { success: false, error: fetchError.message };
    }
    
    if (!subscriptions || subscriptions.length === 0) {
      return { 
        success: true, 
        data: { deleted_subscriptions_count: 0, deleted_payments_count: 0 }
      };
    }
    
    const subscriptionIds = subscriptions.map(s => s.id);
    
    // Подсчитываем платежи перед удалением
    const { count: paymentsCount, error: countError } = await supabase
      .from('payments')
      .select('*', { count: 'exact', head: true })
      .in('subscription_id', subscriptionIds)
      .in('status', ['error', 'cancelled']);
    
    if (countError) {
      console.error('Ошибка при подсчете платежей:', countError);
      return { success: false, error: countError.message };
    }
    
    // Удаляем связанные платежи
    const { error: paymentsError } = await supabase
      .from('payments')
      .delete()
      .in('subscription_id', subscriptionIds)
      .in('status', ['error', 'cancelled']);
    
    if (paymentsError) {
      console.error('Ошибка при удалении платежей:', paymentsError);
      return { success: false, error: paymentsError.message };
    }
    
    // Удаляем подписки
    const { error: subscriptionsError } = await supabase
      .from('subscriptions')
      .delete()
      .in('id', subscriptionIds);
    
    if (subscriptionsError) {
      console.error('Ошибка при удалении подписок:', subscriptionsError);
      return { success: false, error: subscriptionsError.message };
    }
    
    console.log('Пакетное удаление завершено:', {
      deleted_subscriptions_count: subscriptions.length,
      deleted_payments_count: paymentsCount || 0
    });
    
    return { 
      success: true, 
      data: { 
        deleted_subscriptions_count: subscriptions.length, 
        deleted_payments_count: paymentsCount || 0 
      }
    };
  } catch (err: any) {
    console.error('Непредвиденная ошибка при пакетном удалении:', err);
    return { success: false, error: err.message };
  }
};

// Функция для получения статистики подписок без реквизитов карты
export const getSubscriptionsCleanupSummary = async () => {
  try {
    // Получаем подписки без реквизитов карты
    const { data: subscriptions, error: subscriptionsError } = await supabase
      .from('subscriptions')
      .select('id, status')
      .is('payment_method_id', null)
      .is('card_last4', null)
      .in('status', ['cancelled', 'payment_update_needed']);
      
    if (subscriptionsError) {
      console.error('Ошибка при получении подписок:', subscriptionsError);
      return { data: null, error: subscriptionsError.message };
    }
    
    const subscriptionIds = subscriptions?.map(s => s.id) || [];
    
    // Получаем связанные платежи
    const { count: paymentsCount, error: paymentsError } = await supabase
      .from('payments')
      .select('*', { count: 'exact', head: true })
      .in('subscription_id', subscriptionIds)
      .in('status', ['error', 'cancelled']);
      
    if (paymentsError) {
      console.error('Ошибка при получении платежей:', paymentsError);
      return { data: null, error: paymentsError.message };
    }
    
    const summary = {
      total_subscriptions_to_cleanup: subscriptions?.length || 0,
      total_related_payments: paymentsCount || 0,
      cancelled_subscriptions: subscriptions?.filter(s => s.status === 'cancelled').length || 0,
      payment_update_needed_subscriptions: subscriptions?.filter(s => s.status === 'payment_update_needed').length || 0
    };
    
    return { data: summary, error: null };
  } catch (err: any) {
    console.error('Непредвиденная ошибка при получении статистики:', err);
    return { data: null, error: err.message };
  }
};

// Проверка, можно ли удалить подписку (без реквизитов карты и отменена)
export const canDeleteSubscriptionWithoutPaymentMethod = (subscription: any): boolean => {
  return (
    !subscription.payment_method_id &&
    !subscription.cardLast4 &&
    !subscription.card_last4 &&
    (subscription.status === 'cancelled' || subscription.status === 'payment_update_needed')
  );
};
